import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FiBook, FiSearch, FiFilter, FiGrid, FiList,
  FiChevronLeft, FiChevronRight, FiX, FiExternalLink, FiGlobe, FiDownload
} from 'react-icons/fi';
import api from '../../services/api';
import './Katalog.css';

const Katalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    sort: searchParams.get('sort') || 'newest',
    availability: searchParams.get('availability') || ''
  });

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        kategori: filters.category,
        sort: filters.sort,
        availability: filters.availability
      };

      const response = await api.getBooks(params);
      if (response.success) {
        setBooks(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total_rows || 0,
          totalPages: response.pagination?.total_pages || 1
        }));
        // Set categories from response if available
        if (response.filters?.kategoris) {
          setCategories(response.filters.kategoris);
        }
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    updateSearchParams();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.availability) params.set('availability', filters.availability);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      sort: 'newest',
      availability: ''
    });
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const hasActiveFilters = filters.category || filters.availability || filters.search;

  return (
    <div className="katalog">
      {/* Header */}
      <header className="katalog__header">
        <div className="katalog__header-left">
          <h1 className="katalog__title">Katalog Buku</h1>
          <p className="katalog__subtitle">
            {pagination.total} buku tersedia
          </p>
        </div>

        <div className="katalog__header-actions">
          <form className="katalog__search" onSubmit={handleSearch}>
            <FiSearch className="katalog__search-icon" />
            <input
              type="text"
              placeholder="Cari judul, pengarang..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            {filters.search && (
              <button 
                type="button" 
                className="katalog__search-clear"
                onClick={() => handleFilterChange('search', '')}
              >
                <FiX />
              </button>
            )}
          </form>

          <button 
            className={`katalog__filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter />
            Filter
            {hasActiveFilters && <span className="katalog__filter-badge"></span>}
          </button>

          <div className="katalog__view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      {showFilters && (
        <div className="katalog__filters">
          <div className="katalog__filter-group">
            <label>Kategori</label>
            <select 
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">Semua Kategori</option>
              {categories.map(cat => (
                <option key={cat.id_kategori} value={cat.id_kategori}>
                  {cat.nama_kategori}
                </option>
              ))}
            </select>
          </div>

          <div className="katalog__filter-group">
            <label>Ketersediaan</label>
            <select 
              value={filters.availability}
              onChange={(e) => handleFilterChange('availability', e.target.value)}
            >
              <option value="">Semua</option>
              <option value="available">Ada Stok</option>
              <option value="borrowed">Stok Habis</option>
              <option value="openlibrary">Open Library</option>
              <option value="free">Akses Gratis</option>
            </select>
          </div>

          <div className="katalog__filter-group">
            <label>Urutkan</label>
            <select 
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
            >
              <option value="newest">Terbaru</option>
              <option value="oldest">Terlama</option>
              <option value="title_asc">Judul A-Z</option>
              <option value="title_desc">Judul Z-A</option>
              <option value="popular">Terpopuler</option>
            </select>
          </div>

          {hasActiveFilters && (
            <button className="katalog__filter-clear" onClick={clearFilters}>
              <FiX /> Reset Filter
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="katalog__loading">
          <div className="katalog__loading-spinner"></div>
          <p>Memuat data...</p>
        </div>
      ) : books.length === 0 ? (
        <div className="katalog__empty">
          <FiBook className="katalog__empty-icon" />
          <h3>Tidak ada buku ditemukan</h3>
          <p>Coba ubah filter atau kata kunci pencarian Anda</p>
          {hasActiveFilters && (
            <button className="katalog__empty-btn" onClick={clearFilters}>
              Reset Filter
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Books Grid/List */}
          <div className={`katalog__books katalog__books--${viewMode}`}>
            {books.map(book => (
              <Link 
                to={`/katalog/${book.id_buku}`} 
                key={book.id_buku}
                className="katalog__book"
              >
                <div className="katalog__book-cover">
                  <img 
                    src={book.cover_image || '/images/cover-default.png'} 
                    alt={book.judul}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/cover-default.png';
                    }}
                  />
                  {/* Check if truly external (not dosen upload) */}
                  {(book.tipe_buku === 'external' || book.sumber_buku === 'Open Library' || book.sumber_buku === 'Google Books') && 
                   book.sumber_buku !== 'Upload Dosen' && !book.id_pemilik ? (
                    book.is_readable === 1 || book.is_readable === '1' ? (
                      <span className="katalog__book-badge katalog__book-badge--free">
                        <FiGlobe /> Akses Gratis
                      </span>
                    ) : (
                      <span className="katalog__book-badge katalog__book-badge--opensource">
                        <FiExternalLink /> Open Library
                      </span>
                    )
                  ) : book.sumber_buku === 'Upload Dosen' || book.id_pemilik ? (
                    <span className="katalog__book-badge katalog__book-badge--dosen">
                      <FiDownload /> Digital Dosen
                    </span>
                  ) : parseInt(book.stok_tersedia) > 0 ? (
                    <span className="katalog__book-badge katalog__book-badge--available">
                      Tersedia
                    </span>
                  ) : (
                    <span className="katalog__book-badge katalog__book-badge--borrowed">
                      Dipinjam
                    </span>
                  )}
                </div>
                <div className="katalog__book-info">
                  <h3 className="katalog__book-title">{book.judul}</h3>
                  <p className="katalog__book-author">{book.penulis || 'Unknown'}</p>
                  <div className="katalog__book-meta">
                    <span className="katalog__book-category">{book.nama_kategori || 'Umum'}</span>
                    <span className="katalog__book-year">{book.tahun_terbit || '-'}</span>
                  </div>
                  {viewMode === 'list' && book.deskripsi && (
                    <p className="katalog__book-desc">{book.deskripsi}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="katalog__pagination">
              <button 
                className="katalog__pagination-btn"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                <FiChevronLeft />
              </button>

              <div className="katalog__pagination-pages">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`katalog__pagination-page ${pageNum === pagination.page ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button 
                className="katalog__pagination-btn"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                <FiChevronRight />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Katalog;
